<template>
  <v-card class="preview-card">
    <v-card-title class="primary white--text">
        Pré-visualizar diária
        <v-spacer></v-spacer>
        <v-btn icon class="preview-close" @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-progress-linear
            v-if="loading"
            color="warning"
            indeterminate
        ></v-progress-linear>
    </v-card-title>
    <v-card-text v-if="!loading" class="preview-content">
      <div class="preview-inner">
        <v-alert
          v-if="isDiaryRejected(fields.diaria)"
          type="error"
          prominent
          border="left"
          colored-border
          elevation="2"
          class="rejected-preview-alert"
        >
          <div class="rejected-preview-title">
            Esta diária está rejeitada.
          </div>
          <div class="rejected-preview-reason">
            <strong>Motivo:</strong>
            <span>
              {{ rejectionReason }}
            </span>
          </div>
        </v-alert>
        <!-- =========================
            MANOBRADOR
        ========================== -->
        <section v-if="fields.diaria.creation_role == 'manobrador'">
          <!-- Top summary -->
          <v-row class="summary-row" align="stretch">
            <v-col cols="12" md="3">
              <v-sheet class="summary-tile">
                <div class="summary-label">Data</div>
                <div class="summary-value">{{ fields.diaria.data }}</div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="3">
              <v-sheet class="summary-tile">
                <div class="summary-label">Obra</div>
                <div class="summary-value">
                  {{ fields.obra.Codigo }} - {{ fields.obra.Descricao }}
                </div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="3" v-if="fields.diaria.parte_diaria.frente != null">
              <v-sheet class="summary-tile">
                <div class="summary-label">Frente</div>
                <div class="summary-value">{{ getText(fields.diaria.parte_diaria.frente, true) }}</div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="3" v-if="fields.diaria.parte_diaria.classe != null">
              <v-sheet class="summary-tile">
                <div class="summary-label">Classe</div>
                <div class="summary-value">{{ fields.diaria.parte_diaria.classe.Descricao }}</div>
              </v-sheet>
            </v-col>
          </v-row>
          <v-divider class="mt-4"></v-divider>
          <v-row class="mt-4" no-gutters>
            <v-col cols="12" align="start" class="legend-row">
              <span class="mr-4">Máquina - <v-icon color="success">mdi-bulldozer</v-icon></span>
              <span>Acessório - <v-icon color="warning">mdi-tag</v-icon></span>
            </v-col>
            <v-col cols="12">
              <v-card class="section-card" outlined>
                <v-card-title class="section-title">
                  Equipamentos
                </v-card-title>
                <v-card-text class="section-body">
                  <!-- DESKTOP -->
                  <v-simple-table class="d-none d-lg-block premium-table">
                    <template v-slot:default>
                      <thead class="primary">
                        <tr>
                          <th></th>
                          <th class="text-center white--text text-caption font-weight-bold">Equipamento</th>
                          <th class="text-center white--text text-caption font-weight-bold">HS</th>
                          <th class="text-center white--text text-caption font-weight-bold">HO</th>
                          <th class="text-center white--text text-caption font-weight-bold">HA</th>
                          <th class="text-center white--text text-caption font-weight-bold">KMi</th>
                          <th class="text-center white--text text-caption font-weight-bold">KM</th>
                          <th class="text-center white--text text-caption font-weight-bold">Hi</th>
                          <th class="text-center white--text text-caption font-weight-bold">H</th>
                          <th class="text-center white--text text-caption font-weight-bold">Utilizador</th>
                          <th class="text-center white--text text-caption font-weight-bold">HN</th>
                          <th class="text-center white--text text-caption font-weight-bold">HExt</th>
                          <th class="text-center white--text text-caption font-weight-bold">Diesel</th>
                          <th class="text-center white--text text-caption font-weight-bold">Óleo</th>
                          <th class="text-center white--text text-caption font-weight-bold">Adblue</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in fields.diaria.parte_diaria.linhas_eq" :key="item.id" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                          <td>
                            <v-icon color="warning" v-if="isAccessory(item)">mdi-tag</v-icon>
                            <v-icon color="success" v-else>mdi-bulldozer</v-icon>
                          </td>
                          <td class="text-center">
                            <span class="text-caption font-weight-bold">{{ getVehicleName(item) }}</span>
                          </td>
                          <td class="text-center">{{ getNumber(item.Servico) }}</td>
                          <td class="text-center">{{ getNumber(item.Ordem) }}</td>
                          <td class="text-center">{{ getNumber(item.Avariado) }}</td>
                          <td class="text-center">{{ getNumber(item.KmInicial) }}</td>
                          <td class="text-center">{{ getNumber(item.Contador2) }}</td>
                          <td class="text-center">{{ getNumber(item.HoraInicial) }}</td>
                          <td class="text-center">{{ getNumber(item.Contador1) }}</td>
                          <td class="text-center">
                            <Tooltip color="warning" :text="fields.diaria.creator_name" top>
                              {{ fields.diaria.creator }}
                            </Tooltip>
                          </td>
                          <td class="text-center" v-if="!isAccessory(item)">{{ getNumber(item.HNormal) }}</td>
                          <td class="text-center" v-if="!isAccessory(item)">{{ getNumber(item.HExtra) }}</td>
                          <td class="text-center" v-if="!isAccessory(item)">{{ getNumber(item.Combustivel) }}</td>
                          <td class="text-center" v-if="!isAccessory(item)">{{ getNumber(item.oil) }}</td>
                          <td class="text-center" v-if="!isAccessory(item)">{{ getNumber(item.adblue) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <!-- DESKTOP -->
                  <!-- MOBILE -->
                  <v-row>
                    <v-col cols="12">
                      <v-data-iterator
                        :items="fields.diaria.parte_diaria.linhas_eq"
                        item-key="id"
                        :items-per-page="-1"
                        hide-default-footer
                        class="d-block d-lg-none ma-0 pa-0"
                      >
                        <template v-slot:default="{ items }">
                          <v-row class="ma-0 pa-0">
                            <v-col
                              class="ma-0 pa-0"
                              v-for="item in items"
                              :key="item.id"
                              cols="12"
                            >
                              <v-card class="mobile-card" :class="{ 'primavera-integration-error-card': hasPrimaveraIntegrationError(item) }" outlined>
                                <v-list dense>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-icon color="warning" v-if="isAccessory(item)">mdi-tag</v-icon>
                                      <v-icon color="success" v-else>mdi-bulldozer</v-icon>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-textarea
                                        class="mt-4"
                                        :label="isAccessory(item) ? 'Acessório' : 'Máquina'"
                                        :value="getVehicleName(item)"
                                        outlined dense
                                      />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="HS" :value="getNumber(item.Servico)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="HO" :value="getNumber(item.Ordem)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="HA" :value="getNumber(item.Avariado)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="KMi" :value="getNumber(item.KmInicial)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="KM" :value="getNumber(item.Contador2)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field
                                        class="mt-4"
                                        label="Utilizador"
                                        :value="fields.diaria.creator + ' - ' + fields.diaria.creator_name"
                                        outlined dense
                                      />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="HN" :value="getNumber(item.HNormal)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content class="align-center">
                                      <v-text-field class="mt-4" label="HExt" :value="getNumber(item.HExtra)" outlined dense />
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-col>
                          </v-row>
                        </template>
                      </v-data-iterator>
                    </v-col>
                  </v-row>
                  <!-- MOBILE -->
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="mt-6">
            <v-col cols="12">
              <v-card class="section-card" outlined>
                <v-card-title class="section-title">
                  Trabalhos efetuados / Observações
                </v-card-title>
                <v-card-text class="section-body" v-if="fields.diaria.parte_diaria != null">
                  <v-textarea
                    :value="fields.diaria.parte_diaria.observations != null ? fields.diaria.parte_diaria.observations.obs_equipments : ''"
                    outlined
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </section>
        <!-- =========================
            ENCARREGADO
        ========================== -->
        <section v-if="fields.diaria.creation_role == 'encarregado'">
          <!-- Top summary -->
          <v-row class="summary-row" align="stretch">
            <v-col cols="12" md="3">
              <v-sheet class="summary-tile">
                <div class="summary-label">Data</div>
                <div class="summary-value">{{ fields.diaria.data }}</div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="3">
              <v-sheet class="summary-tile">
                <div class="summary-label">Obra</div>
                <div class="summary-value">
                  {{ fields.obra.Codigo }} - {{ fields.obra.Descricao }}
                </div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="6">
              <v-sheet class="summary-tile weather-tile">
                <div v-if="weatherSummary" class="weather-wrap">
                  <v-avatar size="44" class="weather-avatar mr-3">
                    <v-icon size="26" color="primary">{{ weatherSummary.icon }}</v-icon>
                  </v-avatar>
                  <div class="weather-main">
                    <div class="weather-title">{{ weatherSummary.conditionText || 'Meteorologia' }}</div>
                    <div class="weather-meta">
                      <span>Vento: <b>{{ weatherSummary.wind }}</b> km/h</span>
                      <span class="dot">•</span>
                      <span>Máx: <b>{{ weatherSummary.max }}</b>°C</span>
                      <span class="dot">•</span>
                      <span>Min: <b>{{ weatherSummary.min }}</b>°C</span>
                    </div>
                  </div>
                </div>
                <div v-else class="weather-empty">—</div>
              </v-sheet>
            </v-col>
          </v-row>
          <!-- Tabs shell -->
          <v-sheet class="tabs-shell">
            <v-tabs
              v-model="tab"
              dark
              fixed-tabs
              background-color="primary"
              icons-and-text
              :touchless="$vuetify.breakpoint.smAndDown"
            >
              <v-tab :key="0" :disabled="$store.getters.hasRole(['parque'])">
                Mão Obra
                <v-icon>mdi-account-hard-hat</v-icon>
              </v-tab>
              <v-tab :key="1">
                <v-progress-circular v-if="heavyLoading" :size="50" indeterminate color="warning"></v-progress-circular>
                Equipamentos
                <v-icon>mdi-bulldozer</v-icon>
              </v-tab>
              <v-tab :key="2" :disabled="$store.getters.hasRole(['parque'])">
                <v-progress-circular v-if="heavyLoading" :size="50" indeterminate color="warning"></v-progress-circular>
                Material
                <v-icon>mdi-archive</v-icon>
              </v-tab>
              <v-tab :key="3" :disabled="$store.getters.hasRole(['parque'])">
                <v-progress-circular v-if="heavyLoading" :size="50" indeterminate color="warning"></v-progress-circular>
                Subempreitadas
                <v-icon>mdi-briefcase</v-icon>
              </v-tab>
              <v-tab :key="4">
                <v-progress-circular v-if="heavyLoading" :size="50" indeterminate color="warning"></v-progress-circular>
                Observações
                <v-badge v-if="checkBadge" color="error" dot overlap>
                  <v-icon>mdi-eye</v-icon>
                </v-badge>
                <v-icon v-else>mdi-eye</v-icon>
              </v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab" class="tabs-body">
              <!-- === TAB 0: Mão Obra === -->
              <v-tab-item>
                <div @touchstart.stop @touchmove.stop class="tab-pad">
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Mão Obra Interna
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Nome</th>
                                  <th class="text-center">Frente Obra</th>
                                  <th class="text-center">Classe</th>
                                  <th class="text-center">HN</th>
                                  <th class="text-center">HExt</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody v-if="fields.diaria.parte_diaria.pdencMO.interno != null">
                                <tr v-for="item in fields.diaria.parte_diaria.pdencMO.interno.linhas" :key="item.funcionario + '-i'" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td>
                                    <span class="text-caption font-weight-bold">{{item.funcionario}} - {{item.Nome}}</span>
                                  </td>
                                  <td class="text-center">{{ item.frente_obra == null ? '-' : getText(item) }}</td>
                                  <td class="text-center">{{ item.classe_obra == null ? '-' : item.classe_obra }}</td>
                                  <td class="text-center" :colspan="item.fault_type_id == null ? 1 : 2">
                                    <span v-if="item.fault_type_id == null">{{ getNumber(item.hn) }}</span>
                                    <span v-else>{{ item.fault_type_description }}</span>
                                  </td>
                                  <td class="text-center" v-if="item.fault_type_id == null">{{ getNumber(item.hext) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Mão Obra Externa
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Nome</th>
                                  <th class="text-center">Frente Obra</th>
                                  <th class="text-center">Classe</th>
                                  <th class="text-center">HN</th>
                                  <th class="text-center">HExt</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody v-if="fields.diaria.parte_diaria.pdencMO.externo != null">
                                <tr v-for="item in fields.diaria.parte_diaria.pdencMO.externo.linhas" :key="item.funcionario + '-e'" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td>
                                    <span class="text-caption font-weight-bold">{{item.funcionario}} - {{item.Nome}}</span>
                                  </td>
                                  <td class="text-center">{{ item.frente_obra == null ? '-' : getText(item) }}</td>
                                  <td class="text-center">{{ item.classe_obra == null ? '-' : item.classe_obra }}</td>
                                  <td class="text-center">{{ getNumber(item.hn) }}</td>
                                  <td class="text-center">{{ getNumber(item.hext) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
              <!-- === TAB 1: Equipamentos === -->
              <v-tab-item>
                <div @touchstart.stop @touchmove.stop class="tab-pad">
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Equipamento Interno
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Equipamento</th>
                                  <th class="text-center">Frente Obra</th>
                                  <th class="text-center">Classe</th>
                                  <th class="text-center">HS / dia</th>
                                  <th class="text-center">HO</th>
                                  <th class="text-center">HA</th>
                                  <th class="text-center">HP</th>
                                  <th class="text-center">Horas</th>
                                  <th class="text-center">KM</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody v-if="fields.diaria.parte_diaria.pdenc.interno != null">
                                <tr v-for="item in fields.diaria.parte_diaria.pdenc.interno.linhas" :key="item.EquipCode + '-i'" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td class="text-left">
                                    <span class="text-caption font-weight-bold">{{ item.EquipCode }} - {{ item.EquipDescricao }}</span>
                                  </td>
                                  <td class="text-center">{{ item.frente_obra == null ? '-' : getText(item) }}</td>
                                  <td class="text-center">{{ item.classe_obra == null ? '-' : item.classe_obra }}</td>
                                  <td class="text-center">{{ getNumber(item.hs) }}</td>
                                  <td class="text-center">{{ getNumber(item.ho) }}</td>
                                  <td class="text-center">{{ getNumber(item.ha) }}</td>
                                  <td class="text-center">{{ getNumber(item.hp) }}</td>
                                  <td class="text-center">{{ getNumber(item.horas) }}</td>
                                  <td class="text-center">{{ getNumber(item.km) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Equipamento Externo
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Equipamento</th>
                                  <th class="text-center">Frente Obra</th>
                                  <th class="text-center">Classe</th>
                                  <th class="text-center">HS / dia</th>
                                  <th class="text-center">HO</th>
                                  <th class="text-center">HA</th>
                                  <th class="text-center">HP</th>
                                  <th class="text-center">Horas</th>
                                  <th class="text-center">KM</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody v-if="fields.diaria.parte_diaria.pdenc.externo != null">
                                <tr v-for="item in fields.diaria.parte_diaria.pdenc.externo.linhas" :key="item.EquipCode + '-e'" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td class="text-left">
                                    <span class="text-caption font-weight-bold">{{ item.EquipCode }} - {{ item.EquipDescricao }}</span>
                                  </td>
                                  <td class="text-center">{{ item.frente_obra == null ? '-' : getText(item) }}</td>
                                  <td class="text-center">{{ item.classe_obra == null ? '-' : item.classe_obra }}</td>
                                  <td class="text-center">{{ getNumber(item.hs) }}</td>
                                  <td class="text-center">{{ getNumber(item.ho) }}</td>
                                  <td class="text-center">{{ getNumber(item.ha) }}</td>
                                  <td class="text-center">{{ getNumber(item.hp) }}</td>
                                  <td class="text-center">{{ getNumber(item.horas) }}</td>
                                  <td class="text-center">{{ getNumber(item.km) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
              <!-- === TAB 2: Material === -->
              <v-tab-item>
                <div @touchstart.stop @touchmove.stop class="tab-pad">
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Materiais
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Frente Obra</th>
                                  <th class="text-center">Classe</th>
                                  <th class="text-center">Artigo</th>
                                  <th class="text-center">Un</th>
                                  <th class="text-center">QTD</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="item in fields.diaria.parte_diaria.material" :key="item.material + '-' + item.frente_obra" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td class="text-center">{{ item.frente_obra }}</td>
                                  <td class="text-center">{{ item.classe_obra }}</td>
                                  <td class="text-left">
                                    <span class="text-caption font-weight-bold">{{ item.material }}</span>
                                  </td>
                                  <td class="text-center">{{ item.unidade }}</td>
                                  <td class="text-center">{{ getNumber(item.qty) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
              <!-- === TAB 3: Subempreitadas === -->
              <v-tab-item>
                <div @touchstart.stop @touchmove.stop class="tab-pad">
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Subempreitadas
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-simple-table fixed-header class="premium-table">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th class="text-center">Trabalho</th>
                                  <th class="text-center">QTD</th>
                                  <th class="text-center">Nota</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="item in fields.diaria.parte_diaria.subempreitada" :key="item.Descricao" :class="{ 'primavera-integration-error-row': hasPrimaveraIntegrationError(item) }">
                                  <td class="text-left">
                                    <span class="text-caption font-weight-bold">{{ item.Descricao }}</span>
                                  </td>
                                  <td class="text-center">{{ getNumber(item.qty) }}</td>
                                  <td class="text-center">{{ item.local }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
              <!-- === TAB 4: Observações === -->
              <v-tab-item>
                <div @touchstart.stop @touchmove.stop class="tab-pad">
                  <v-row v-if="fields.diaria.parte_diaria.observations != null && !$store.getters.hasRole(['parque'])">
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Observações de Mão de Obra
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-textarea
                            readonly
                            :value="fields.diaria.parte_diaria.observations.obs_work_hand"
                            outlined dense
                            label="Observações"
                            hint="Escreva as suas observações relativas à mão de obra"
                            persistent-hint
                          ></v-textarea>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row v-if="fields.diaria.parte_diaria.observations != null">
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Observações de Equipamentos
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-textarea
                            readonly
                            :value="fields.diaria.parte_diaria.observations.obs_equipments"
                            outlined dense
                            label="Observações"
                            hint="Escreva as suas observações relativas aos equipamentos"
                            persistent-hint
                          ></v-textarea>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-card class="section-card" outlined>
                        <v-card-title class="section-title">
                          Observações Obra/Trabalhos Efetuados
                        </v-card-title>
                        <v-card-text class="section-body">
                          <v-col
                            cols="12"
                            v-for="(item,index) in fields.diaria.parte_diaria.work_order_obs"
                            :key="index + 85552"
                            class="pa-0"
                          >
                            <v-row class="ma-0 pa-0">
                              <v-col cols="12" md="6">
                                <v-text-field
                                  v-model="item.work_order_service"
                                  outlined dense
                                  label="Nº Autorização Trabalho/Ordem Serviço"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  v-model="item.description"
                                  outlined dense
                                  label="Descrição"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-sheet>
          <v-row class="nav-row">
            <v-col cols="12" md="6" class="d-none d-lg-block" align="start">
              <v-btn :disabled="$store.getters.hasRole(['parque']) ? tab == 1 : tab == 0" @click="backward()" fab class="mt-4 primary">
                <v-icon small>mdi-less-than</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" md="6" class="d-none d-lg-block" align="end">
              <v-btn :disabled="tab == 4" @click="forward()" fab class="mt-4 primary">
                <v-icon small>mdi-greater-than</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </section>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
import Diaries from "@/api/Diaries.js"
import Obra from "@/api/Obra.js"
import Equipamentos from "@/api/Equipamentos.js"
import Tooltip from '@/components/UI/Tooltip.vue';
export default{
    name:"PreviewDiary",
    components:{
        Tooltip
    },
    props:{
        diary_id: String
    },
    data(){
        return{
            tab: 0,
            loading: true,
            heavyLoading: true,
            fields:{},
            equipamentos:[],
            unassignedMachines:[]
        }
    },
    mounted(){
        if(this.$store.getters.hasRole(['parque']))
            this.tab = 1
        this.fillBaseData()
    },
    methods:{
        isDiaryRejected(item){
            if(!item) return false
            return item.is_rejected === true ||
                item.is_rejected === 1 ||
                item.is_rejected === '1'
        },
        getText(ob, with_desc_field = false) {
            if(!with_desc_field){
                if (ob.frente_obra && ob.SubEmp && ob.frente_obra.includes(ob.SubEmp)) {
                    return ob.frente_obra;
                }
                return `${ob.SubEmp} - ${ob.frente_obra}`;
            }else{
                if (ob.Descricao && ob.SubEmp && ob.Descricao.includes(ob.SubEmp)) {
                    return ob.Descricao;
                }
                return `${ob.SubEmp} - ${ob.Descricao}`;
            }
        },
        backward(){
            if(this.$store.getters.hasRole(['parque'])){
                this.tab = 1
                return 
            }
            this.tab = this.tab - 1
        },
        forward(){
            if(this.$store.getters.hasRole(['parque'])){
                this.tab = 4
                return 
            }
            this.tab = this.tab + 1
        },
        isAccessory(item){
            let flag = true;
            if(item.HNormal != null)
                flag = false;
            if(item.HExtra != null)
                flag = false;
            if(item.oil != null)
                flag = false;
            if(item.Combustivel != null)
                flag = false;
            return flag;
        },
        getNumber(n){
            let num = parseFloat(n)
            num = isNaN(num) ? 0 : num;
            return num
        },
        hasPrimaveraIntegrationError(item){
            return item != null &&
                item.primavera_integration_error != null &&
                String(item.primavera_integration_error).trim() !== ''
        },
        getVehicleName(item){
            let object = null;
            return item.EntityKey + ' - ' + item.Descricao;
            /*
            this.equipamentos.forEach(element => {
                if(element.Id == item.IdEquipamento){
                    object = element.Descricao
                }
            });
            if(object == null){
                this.unassignedMachines.forEach(element => {
                    if(element.Id == item.IdEquipamento)
                        object = element.Descricao
                });
            }*/
            //return object
        },
        async fillBaseData(){
            let data = {
                type: 'light'
            }
            await Diaries.preview(this.diary_id, data).then((data)  =>  {
                this.fields = data.data
                this.loading = false
            })
            data = {
                work_id: this.fields.obra.Id,
                codigo: this.$store.state.user.data.funcionario.Codigo,
                place: 'manobrador'
            }
            Obra.getObraEquipamentos(data).then((resp)  =>  {
                // Manobrador acede apenas a equipamentos externos
                this.equipamentos = resp.data.externo
            })
            Equipamentos.getUnassigned(this.fields.obra.Id).then((resp)    =>  {
                this.unassignedMachines = resp.data
            })
            this.heavyLoad()
        },
        heavyLoad(){
            let data = {
                type: 'heavyLoad'
            }
            Diaries.preview(this.diary_id, data).then((data)  =>  {
                this.fields = data.data
                this.heavyLoading = false
            })
        }
    },
    computed:{       
      rejectionReason() {
          return this.fields?.diaria?.latest_rejection_reason ||
              this.fields?.diaria?.latest_rejection?.comment ||
              'Sem comentário disponível.'
      }, 
        checkBadge() {
            if (this.fields.diaria?.parte_diaria?.observations == null) return false
            return this.fields.diaria.parte_diaria.observations.obs_work_hand != null ||
                this.fields.diaria.parte_diaria.observations.obs_equipments
        },
        weatherSummary () {
            const raw = this.fields?.diaria?.weather
            if (!raw) return null
            let meta = null
            try {
            meta = (typeof raw === 'string') ? JSON.parse(raw) : raw
            } catch (e) {
            return null
            }
            // se vier embrulhado em { metadata: "..." } ou { metadata: {...} }
            if (meta && meta.metadata) {
            try {
                meta = (typeof meta.metadata === 'string') ? JSON.parse(meta.metadata) : meta.metadata
            } catch (e) {
                // se falhar, continua com o meta original
            }
            }
            const day = meta?.day
            if (!day) return null
            const conditionText = day.condition?.text || ''
            const lower = conditionText.toLowerCase()
            let icon = 'mdi-weather-partly-cloudy'
            if (lower.includes('chuva') || lower.includes('chuvisco') || lower.includes('aguace')) {
            icon = 'mdi-weather-rainy'
            } else if (lower.includes('neve')) {
            icon = 'mdi-weather-snowy'
            } else if (lower.includes('neblina') || lower.includes('nevoeiro') || lower.includes('nevoa')) {
            icon = 'mdi-weather-fog'
            } else if (lower.includes('sol') || lower.includes('céu limpo') || lower.includes('ceu limpo')) {
            icon = 'mdi-weather-sunny'
            } else if (lower.includes('nublado') || lower.includes('encoberto')) {
            icon = 'mdi-weather-cloudy'
            }
            return {
            icon,
            conditionText,
            max: day.maxtemp_c,
            min: day.mintemp_c,
            wind: day.maxwind_kph,
            }
        }
    }
}
</script>
<style scoped>
/* Card principal */
.preview-card{
  height: 100% !important;
  border-radius: 18px;
  overflow: hidden;
}
/* Conteúdo geral */
.preview-content{
  background: linear-gradient(180deg, rgba(2, 119, 189, 0.06), rgba(2, 119, 189, 0.00) 220px);
  padding: 18px;
}
.preview-inner{
  max-width: 1450px;
  margin: 0 auto;
}
/* Tiles do topo (Data / Obra / Meteo / Frente / Classe) */
.summary-row{
  margin-top: 14px;
  margin-bottom: 14px;
}
.summary-tile{
  background: rgba(255,255,255,0.92);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow:
    0 10px 22px rgba(0,0,0,0.06),
    0 2px 6px rgba(0,0,0,0.04);
  height: 100%;
}
.summary-label{
  font-size: 11px;
  letter-spacing: .10em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.55);
  margin-bottom: 6px;
}
.summary-value{
  font-size: 14px;
  font-weight: 800;
  color: rgba(0,0,0,0.86);
  line-height: 1.25;
}
/* Meteo */
.weather-tile{
  display: flex;
  align-items: center;
  justify-content: center;
}
.weather-wrap{
  width: 100%;
  display: flex;
  align-items: center;
}
.weather-avatar{
  background: rgba(2, 119, 189, 0.08);
  border: 1px solid rgba(2, 119, 189, 0.20);
}
.weather-main{
  flex: 1;
  min-width: 0;
}
.weather-title{
  font-size: 13px;
  font-weight: 800;
  color: rgba(0,0,0,0.86);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.weather-meta{
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0,0,0,0.62);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.weather-meta .dot{
  opacity: .55;
}
.weather-empty{
  color: rgba(0,0,0,0.45);
  font-weight: 700;
}
/* legenda (máquina/acessório) */
.legend-row{
  padding: 6px 4px 12px 4px;
  color: rgba(0,0,0,0.68);
  font-weight: 700;
}
/* “Shell” das tabs */
.tabs-shell{
  margin-top: 12px;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 10px 24px rgba(0,0,0,0.05),
    0 2px 6px rgba(0,0,0,0.04);
}
.tabs-body{
  padding: 12px;
}
.tab-pad{
  padding: 6px;
}
/* dá mais presença à barra das tabs */
.tabs-shell :deep(.v-tabs-bar){
  box-shadow: inset 0 -1px 0 rgba(255,255,255,0.18);
}
.tabs-shell :deep(.v-tab){
  letter-spacing: .04em;
  font-weight: 800;
}
/* Cards de secção */
.section-card{
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow:
    0 10px 24px rgba(0,0,0,0.06),
    0 2px 6px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06) !important;
}
.section-title{
  font-weight: 900;
  letter-spacing: .02em;
  background: linear-gradient(90deg, rgba(255,193,7,0.25), rgba(255,193,7,0.10));
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.section-body{
  padding-top: 14px;
}
/* Tabelas “premium” */
.premium-table :deep(thead th){
  font-size: 11px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.70);
  background: rgba(2,119,189,0.06);
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.premium-table :deep(tbody tr:nth-child(odd)){
  background: rgba(0,0,0,0.015);
}
.premium-table :deep(tbody tr:hover){
  background: rgba(2,119,189,0.06);
}
.premium-table :deep(tbody tr.primavera-integration-error-row),
.premium-table :deep(tbody tr.primavera-integration-error-row:hover){
  background: rgba(255, 193, 7, 0.18) !important;
}
.primavera-integration-error-card{
  background: rgba(255, 193, 7, 0.18) !important;
}
.premium-table :deep(td){
  border-bottom: 1px solid rgba(0,0,0,0.05);
  padding-top: 12px;
  padding-bottom: 12px;
}
/* Mobile cards */
.mobile-card{
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 10px 22px rgba(0,0,0,0.06),
    0 2px 6px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.06) !important;
  margin-top: 10px;
}
/* Navegação inferior */
.nav-row{
  margin-top: 6px;
}
.preview-close{
  margin-left: 6px;
  color: rgba(255,255,255,0.92) !important;
}
.preview-close :deep(.v-icon){
  color: rgba(255,255,255,0.92) !important;
}
.preview-close:hover{
  background: rgba(255,255,255,0.12) !important;
}
.rejected-preview-alert{
  margin-top: 14px;
  margin-bottom: 18px;
  border-radius: 14px;
  background: rgba(255, 235, 238, 0.96) !important;
}
.rejected-preview-title{
  font-size: 16px;
  font-weight: 900;
  color: #b71c1c;
  margin-bottom: 8px;
}
.rejected-preview-reason{
  font-size: 14px;
  color: rgba(0,0,0,0.84);
  line-height: 1.45;
}
.rejected-preview-reason strong{
  color: #b71c1c;
  margin-right: 4px;
}
</style>
